/**
 * CR158: Kickoff Stuck Run Button
 * Recovery mechanism for batch runs stuck at RUNNING with all steps NOT_STARTED
 * Manually enqueues the first AUTO step to restart execution
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, PlayCircle, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface BatchRunStep {
  id: string;
  step_key: string;
  step_name: string;
  status: string;
  mode: string;
}

interface KickoffStuckRunButtonProps {
  batchRunId: string;
  runStatus: string;
  sourcesEnabled: string[];
  steps: BatchRunStep[];
  onKickedOff: () => void;
}

// Determine the first collection step based on enabled sources
function getFirstCollectionStep(sourcesEnabled: string[]): string | null {
  if (sourcesEnabled.includes("FLYER")) return "S1";
  if (sourcesEnabled.includes("WEB")) return "S2";
  if (sourcesEnabled.includes("FORUM")) return "S3";
  return null;
}

export function KickoffStuckRunButton({
  batchRunId,
  runStatus,
  sourcesEnabled,
  steps,
  onKickedOff,
}: KickoffStuckRunButtonProps) {
  const { toast } = useToast();
  const [showDialog, setShowDialog] = useState(false);
  const [isKicking, setIsKicking] = useState(false);

  // Check if run is stuck: RUNNING status but all steps NOT_STARTED
  const allStepsNotStarted = steps.every(
    (s) => s.status === "NOT_STARTED" || s.status === "SKIPPED"
  );
  const isStuck =
    runStatus === "RUNNING" && allStepsNotStarted && steps.length > 0;

  // Find the first AUTO step that should be enqueued
  const firstStepKey = getFirstCollectionStep(sourcesEnabled);
  const firstStep = steps.find(
    (s) => s.step_key === firstStepKey && s.mode === "AUTO"
  );

  // Only show button if run is stuck and has a kickoffable step
  if (!isStuck || !firstStep) return null;

  const handleKickoff = async () => {
    setIsKicking(true);
    try {
      // 1. Set the first step to ENQUEUED
      const { error: stepError, count } = await supabase
        .from("cr075_batch_run_steps")
        .update({
          status: "ENQUEUED",
          started_at: null,
        })
        .eq("id", firstStep.id)
        .eq("status", "NOT_STARTED"); // Only update if still NOT_STARTED

      if (stepError) throw stepError;

      if (count === 0) {
        toast({
          title: "Already started",
          description: "Step was already enqueued or started by another process",
        });
        setShowDialog(false);
        onKickedOff();
        return;
      }

      // 2. Invoke scheduler-runner to pick up the enqueued step
      const { error: runnerError } = await supabase.functions.invoke(
        "scheduler-runner",
        { body: {} }
      );

      if (runnerError) {
        console.warn("Runner invocation failed, step is still enqueued:", runnerError);
        // Don't throw - the step is enqueued and will be picked up on next poll
      }

      toast({
        title: "Run kicked off",
        description: `${firstStep.step_name} enqueued for execution`,
      });

      setShowDialog(false);
      onKickedOff();
    } catch (err) {
      console.error("Failed to kickoff run:", err);
      toast({
        title: "Kickoff failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsKicking(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowDialog(true)}
        className="text-primary hover:text-primary"
      >
        <PlayCircle className="h-4 w-4 mr-2" />
        Kickoff Stuck Run
      </Button>

      <AlertDialog open={showDialog} onOpenChange={setShowDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <PlayCircle className="h-5 w-5 text-primary" />
              Kickoff Stuck Run
            </AlertDialogTitle>
            <AlertDialogDescription>
              This run appears to be stuck with status RUNNING but no steps have
              started.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Recovery Action</AlertTitle>
            <AlertDescription>
              This will enqueue <strong>{firstStep.step_name}</strong> (
              {firstStep.step_key}) and trigger the scheduler runner to pick it
              up.
            </AlertDescription>
          </Alert>

          <div className="rounded-lg border p-4 bg-muted/50">
            <div className="text-sm font-medium mb-2">Diagnosis:</div>
            <ul className="text-sm space-y-1 text-muted-foreground">
              <li>• Run status: {runStatus}</li>
              <li>• Total steps: {steps.length}</li>
              <li>• Steps NOT_STARTED: {steps.filter((s) => s.status === "NOT_STARTED").length}</li>
              <li>• First AUTO step: {firstStep.step_name} ({firstStep.step_key})</li>
            </ul>
          </div>

          <AlertDialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDialog(false)}
              disabled={isKicking}
            >
              Cancel
            </Button>
            <Button onClick={handleKickoff} disabled={isKicking}>
              {isKicking ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Kicking off...
                </>
              ) : (
                <>
                  <PlayCircle className="h-4 w-4 mr-2" />
                  Kickoff Run
                </>
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
